// CrystalVault — Trade Execution Logs

import { VAULT_CONFIGS, VAULT_ABI, USDC_DECIMALS, SHARE_DECIMALS, NETWORK } from '../config.js';
import { getVaultState, getFills, prefetchFills, prefetchVaultStates } from '../hlFeed.js';
import { getAddress, getReadProvider } from '../wallet.js';
import { isStealth, isDepositor, launchDateLabel } from '../stealth-mode.js';
import { getTzMode, setTzMode, tzZone, tzLabel } from '../time.js';

// Vault identity used in the "Vault" column (10-gemstone fleet)
const VAULT_ICONS = {
  onyx:      { icon: 'hexagon',       color: '#64748B', text: 'text-[#64748B]', bg10: 'bg-[#64748B]/10' },
  amber:     { icon: 'grain',         color: '#F59E0B', text: 'text-[#F59E0B]', bg10: 'bg-[#F59E0B]/10' },
  moonstone: { icon: 'nights_stay',   color: '#C7D2FE', text: 'text-[#C7D2FE]', bg10: 'bg-[#C7D2FE]/10' },
  peridot:   { icon: 'eco',           color: '#84CC16', text: 'text-[#84CC16]', bg10: 'bg-[#84CC16]/10' },
  ruby:      { icon: 'local_fire_department', color: '#DC2626', text: 'text-[#DC2626]', bg10: 'bg-[#DC2626]/10' },
  diamond:   { icon: 'auto_awesome',  color: '#E5E7EB', text: 'text-[#E5E7EB]', bg10: 'bg-[#E5E7EB]/10' },
  sapphire:  { icon: 'water_drop',    color: '#2563EB', text: 'text-[#2563EB]', bg10: 'bg-[#2563EB]/10' },
  emerald:   { icon: 'spa',           color: '#10B981', text: 'text-[#10B981]', bg10: 'bg-[#10B981]/10' },
  pearl:     { icon: 'blur_circular', color: '#FED7AA', text: 'text-[#FED7AA]', bg10: 'bg-[#FED7AA]/10' },
  opal:      { icon: 'lens_blur',     color: '#A78BFA', text: 'text-[#A78BFA]', bg10: 'bg-[#A78BFA]/10' },
};

let cachedTrades = null;

async function fetchTrades() {
  const liveVaults = VAULT_CONFIGS.filter(v => v.status === 'live' && v.address);
  if (!liveVaults.length) return [];

  // Parallelize getFills across vaults — was sequential `for...of` with
  // await each, so 6 vaults × ~300ms HL round-trip = ~1.8s before this page
  // could render anything. Promise.all collapses to ~max(300ms).
  const perVaultTrades = await Promise.all(liveVaults.map(async (vault) => {
    let fills;
    try {
      fills = await getFills(vault.address);  // shared cache with Overview page
    } catch (e) {
      console.warn('HL fills fetch failed for', vault.name, e);
      return [];
    }
    if (!fills || !fills.length) return [];
    return pairFills(fills, vault);
  }));

  const allTrades = perVaultTrades.flat();
  // Sort newest first
  allTrades.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  cachedTrades = allTrades;
  return allTrades;
}

// Pair HL fills (Open X / Close X) into complete trade records.
//
// One position = one row. HL splits a single order across multiple fills
// when it walks the orderbook (3 fills against one oid is common on
// thin coins or larger size), AND a position can close in multiple
// LEGS (TP1 partial at one oid, SL/trail closing the remainder at a
// different oid). Both shapes look like "multiple trades" if you render
// per-fill; both are the SAME logical position from the runner's view.
//
// Strategy: accumulate ALL close fills onto the open trade record.
// Emit ONE row only when remaining_size reaches 0. Exit price becomes
// size-weighted across all close fills; pnl is the sum; status reflects
// the FINAL close leg (maker→TP, taker→SL). Per-fill stages are stashed
// in `close_fills` for a future expand-detail UI.
//
// Caught 2026-05-20: Onyx BCH trade displayed 3 rows ($-1.383/$-0.323/
// $-0.167) instead of one (-$1.735 net). Inflated trade count + broke
// "win rate by row" semantics whenever a close walked through breakeven.
// See feedback_pairfills_one_position_one_row.md.
function pairFills(fills, vault) {
  const sorted = [...fills].sort((a, b) => a.time - b.time);
  const open = {};   // coin → open trade with mutable remaining_size
  const trades = [];

  for (const f of sorted) {
    const coin = f.coin;
    const isOpen = f.dir && f.dir.startsWith('Open');
    const isClose = f.dir && f.dir.startsWith('Close');
    const isLong = f.dir && f.dir.includes('Long');
    const px = parseFloat(f.px);
    const sz = parseFloat(f.sz);
    const fee = parseFloat(f.fee || 0);
    const builderFee = parseFloat(f.builderFee || 0);

    if (isOpen) {
      // Accumulate same-direction opens for the same coin into one logical
      // position. HL frequently reports a single runner entry as multiple
      // Open fills — either because (a) HL's matching engine fills our order
      // against multiple resting orders on the book, or (b) the runner sent
      // multiple orders (scale-in / pyramiding / retries). In all cases HL's
      // clearinghouseState treats them as ONE position with a weighted-average
      // entry price. We do the same.
      //
      // Pre-2026-05-11 this function REPLACED the previous open on any
      // duplicate, silently dropping size. Symptom: open positions disappeared
      // from the All Trades page once any partial close filled. See
      // pairFills_accumulate_multi_fill_opens commit msg for the live bug.
      const existing = open[coin];
      if (existing && existing.side === (isLong ? 'long' : 'short')) {
        // Same-direction add → accumulate. Weighted-average entry across all
        // open fills, sum fees, keep original entry_ts. `size` reflects total
        // opened so far; `remaining_size` reflects what's still open.
        const newTotalSize = existing.size + sz;
        const newWeightedEntry = (existing.entry_price * existing.size + px * sz) / newTotalSize;
        existing.size = newTotalSize;
        existing.remaining_size = existing.remaining_size + sz;
        existing.entry_price = newWeightedEntry;
        existing.notional = newWeightedEntry * newTotalSize;
        existing.fee = (existing.fee || 0) + fee;
        existing.builder_fee = (existing.builder_fee || 0) + builderFee;
        // Maker flag becomes "any leg was taker → taker", since fees reflect mix
        existing.maker = existing.maker && !f.crossed;
        existing.entry_maker = existing.entry_maker && !f.crossed;
        continue;
      }
      // Opposite-direction "Open" after a close, or first fill for this coin
      if (existing) {
        // Direction flipped without a Close — unusual, but emit a warn and
        // start fresh. Treat as a new position.
        console.warn('pairFills: direction flip without close for', coin);
      }
      open[coin] = {
        ts: new Date(f.time).toISOString(),
        vault: vault.name,
        vaultKey: vault.key,
        symbol: coin + '/USDT:USDT',
        coin,
        side: isLong ? 'long' : 'short',
        entry_price: px,
        size: sz,                     // total opened across all fills so far
        remaining_size: sz,           // mutable: shrinks on partial closes
        notional: px * sz,
        fee,
        builder_fee: builderFee,
        maker: !f.crossed,
        entry_maker: !f.crossed,        // entry leg's fill type (asset-column tag)
        exit_maker: null,               // no exit yet
        status: 'open',
        exit_price: null,
        close_ts: null,
        pnl: null,
      };
    } else if (isClose && open[coin]) {
      const trade = open[coin];
      const pnl = parseFloat(f.closedPnl || 0);
      const closeQty = Math.min(sz, trade.remaining_size);
      const remainingAfter = trade.remaining_size - closeQty;
      const isMaker = !f.crossed;

      // Accumulate this close fill onto the open trade. No row emitted
      // yet — wait until the position is fully closed (remaining_size 0).
      // close_fills retains per-leg data for a future expand-detail UI.
      trade.close_fills = trade.close_fills || [];
      trade.close_fills.push({
        oid: f.oid,
        sz: closeQty,
        px,
        pnl,
        fee,
        builder_fee: builderFee,
        maker: isMaker,
        ts: f.time,
      });
      trade.realized_pnl = (trade.realized_pnl || 0) + pnl;
      trade.close_fee_sum = (trade.close_fee_sum || 0) + fee;
      trade.close_builder_fee_sum = (trade.close_builder_fee_sum || 0) + builderFee;
      trade.remaining_size = remainingAfter;
      trade.last_close_ts = f.time;
      trade.last_close_maker = isMaker;

      if (remainingAfter > 1e-9) {
        // TP1 / book-walking mid-close — wait for more close fills.
        continue;
      }

      // Position fully closed → emit ONE aggregated row.
      // Exit price: size-weighted across every close fill so a 3-level
      // walk through (364.38, 364.39, 364.41) shows the effective ~364.385.
      // Status: takes the FINAL close fill's maker flag — that's the leg
      // that actually finished the position (TP1+SL → status=sl_*).
      const totalCloseSz = trade.close_fills.reduce((s, c) => s + c.sz, 0);
      const weightedExit = totalCloseSz > 0
        ? trade.close_fills.reduce((s, c) => s + c.sz * c.px, 0) / totalCloseSz
        : px;
      const status = trade.last_close_maker
        ? (trade.realized_pnl >= 0 ? 'tp_profit' : 'tp_loss')
        : (trade.realized_pnl >= 0 ? 'sl_profit' : 'sl_loss');
      trades.push({
        ts: trade.ts,                              // original open time
        vault: trade.vault,
        vaultKey: trade.vaultKey,
        symbol: trade.symbol,
        coin: trade.coin,
        side: trade.side,
        entry_price: trade.entry_price,
        exit_price: weightedExit,
        size: trade.size,                          // total opened size
        notional: weightedExit * trade.size,
        fee: trade.close_fee_sum,                  // summed close fees
        entry_fee: trade.fee,                      // full entry fee — position fully closed
        builder_fee: trade.close_builder_fee_sum,  // summed builder fees on closes
        maker: trade.last_close_maker,             // FINAL close leg's flag
        entry_maker: trade.entry_maker,            // entry leg (preserved through close)
        exit_maker: trade.last_close_maker,        // FINAL close leg
        status,
        close_ts: new Date(trade.last_close_ts).toISOString(),
        pnl: trade.realized_pnl,
        n_close_fills: trade.close_fills.length,
        close_fills: trade.close_fills,            // dormant — future expand-detail UI
      });
      delete open[coin];
    } else if (isClose) {
      // Close with no matching open — shouldn't happen for a fresh vault, but
      // could appear if fills are truncated. Show as standalone.
      const orphanPnl = parseFloat(f.closedPnl || 0);
      const isMaker = !f.crossed;
      const orphanStatus = isMaker
        ? (orphanPnl >= 0 ? 'tp_profit' : 'tp_loss')
        : (orphanPnl >= 0 ? 'sl_profit' : 'sl_loss');
      trades.push({
        ts: new Date(f.time).toISOString(),
        vault: vault.name,
        vaultKey: vault.key,
        symbol: coin + '/USDT:USDT',
        coin,
        side: isLong ? 'long' : 'short',
        entry_price: px,
        exit_price: px,
        size: sz,
        notional: px * sz,
        fee,
        builder_fee: builderFee,
        maker: isMaker,
        entry_maker: null,                         // no matching open → entry unknown
        exit_maker: isMaker,                       // this close fill
        status: orphanStatus,
        close_ts: new Date(f.time).toISOString(),
        pnl: orphanPnl,
      });
    }
  }

  // Remaining open positions — use current remaining_size (not original).
  for (const trade of Object.values(open)) {
    trade.size = trade.remaining_size;
    trade.notional = trade.entry_price * trade.remaining_size;
    trades.push(trade);
  }

  return trades;
}

// Single source of truth for a trade row's displayed P&L — always net of ALL fees.
// Closed rows: closedPnl − close fee − entry fee (pro-rata).
// Open rows: unrealizedPnl − estimated exit fee − entry fee (already paid).
// Perf fee is NOT included — withdrawal-time concern, not trading cost.
const EXIT_FEE_EST_BPS = 6;  // 0.06% est. exit cost ≈ ~0.035% avg HL exchange (maker-TP/taker-SL mix) + 0.025% builder (builder lowered 3.5→2.5 bps 2026-05-23)
function rowPnl(t) {
  if (t.status === 'open') {
    const raw = t.floating_pnl != null ? t.floating_pnl : 0;
    const notional = (t.current_price || 0) * (t.size || 0);
    const exitFee = notional * EXIT_FEE_EST_BPS / 10000;
    return raw - exitFee - (t.fee || 0);  // t.fee = entry fee for open positions
  }
  if (t.pnl == null) return 0;
  return t.pnl - (t.fee || 0) - (t.entry_fee || 0);
}

function formatTradeRow(t) {
  const date = new Date(t.ts);
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tzZone() });
  const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tzZone() }) + ' ' + tzLabel(date);

  const sideClass = 'bg-surface-container-high text-on-surface-variant border-outline-variant/20';
  const netPnl = rowPnl(t);

  let statusLabel, statusClass;
  if (t.status === 'open') {
    statusLabel = 'OPEN'; statusClass = 'text-primary';
  } else if (t.status === 'tp_profit') {
    // Maker exit with profit: TP hit. If partial, it's TP1/TP2 (not the
    // trailing final close). Distinguish when we can.
    statusLabel = t.partial ? 'TP (partial)' : 'TP';
    statusClass = 'text-tertiary';
  } else if (t.status === 'tp_loss') {
    // Rare: maker exit at loss. Happens with trailing SL → above entry that
    // later gets converted to a maker close. Label as generic close.
    statusLabel = 'CLOSED (maker)'; statusClass = 'text-error';
  } else if (t.status === 'sl_profit') {
    // Taker exit with profit: trailing SL triggered above entry. Classic
    // trend-follow win via stop movement.
    statusLabel = 'SL PROFIT'; statusClass = 'text-tertiary';
  } else if (t.status === 'sl_loss') {
    statusLabel = 'SL LOSS'; statusClass = 'text-error';
  } else {
    statusLabel = netPnl >= 0 ? 'CLOSED TP' : 'CLOSED LOSS';
    statusClass = netPnl >= 0 ? 'text-tertiary' : 'text-error';
  }

  const vi = VAULT_ICONS[t.vaultKey] || VAULT_ICONS.onyx;
  const entry = t.entry_price || 0;
  const exit_ = t.exit_price || 0;
  // Quantity to 2 decimals (was 4) — keeps the table tight enough to fit
  // the new Exit Price column without bloating the row height.
  const qty = t.size ? t.size.toFixed(2) : '—';

  // Per-row P&L: net of fees for closed rows (keeps per-row sums consistent
  // with the headline). Open rows use HL's unrealized as-is.
  let pnlStr = '—';
  let pnlColor = 'text-on-surface-variant';
  if ((t.status === 'open' && t.floating_pnl != null) || (t.status !== 'open' && t.pnl != null)) {
    pnlStr = (netPnl >= 0 ? '+' : '-') + '$' + Math.abs(netPnl).toFixed(3);
    pnlColor = netPnl >= 0 ? 'text-tertiary' : 'text-error';
  }

  let closeDateStr = '—'; let closeTimeStr = '';
  if (t.close_ts) {
    const cd = new Date(t.close_ts);
    closeDateStr = cd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tzZone() });
    closeTimeStr = cd.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tzZone() }) + ' ' + tzLabel(cd);
  }

  return `<tr class="hover:bg-surface-container transition-colors" data-trade-status="${t.status === 'open' ? 'open' : (netPnl >= 0 ? 'won' : 'lost')}">
    <td class="px-3 py-5">
      <div class="text-sm font-medium text-on-surface">${dateStr}</div>
      <div class="text-[0.7rem] text-outline font-mono">${timeStr}</div>
    </td>
    <td class="px-3 py-5">
      ${t.close_ts ? `<div class="text-sm font-medium text-on-surface">${closeDateStr}</div><div class="text-[0.7rem] text-outline font-mono">${closeTimeStr}</div>` : `<span class="text-xs text-primary font-bold">OPEN</span>`}
    </td>
    <td class="px-3 py-5">
      <div class="flex items-center gap-2.5">
        <div class="w-7 h-7 ${vi.bg10} rounded-md flex items-center justify-center">
          <span class="material-symbols-outlined ${vi.text} text-base">${vi.icon}</span>
        </div>
        <span class="text-sm font-medium">${t.vault}</span>
      </div>
    </td>
    <td class="px-3 py-5">
      <div class="font-headline font-bold text-on-surface tracking-tight">${t.coin}/USDT</div>
      ${(t.entry_maker != null || t.exit_maker != null) ? `<div class="flex items-center gap-2 mt-0.5 text-[0.55rem] font-bold uppercase tracking-wide">${t.entry_maker != null ? `<span class="inline-flex items-center gap-0.5 ${t.entry_maker ? 'text-tertiary' : 'text-on-surface-variant/70'}" title="Entry fill — ${t.entry_maker ? 'maker (post-only; no taker fee to enter)' : 'taker (crossed the spread to enter)'}"><span class="material-symbols-outlined text-[0.7rem] leading-none">login</span>${t.entry_maker ? 'maker' : 'taker'}</span>` : ''}${t.exit_maker != null ? `<span class="inline-flex items-center gap-0.5 ${t.exit_maker ? 'text-tertiary' : 'text-on-surface-variant/70'}" title="Exit fill — ${t.exit_maker ? 'maker (take-profit limit)' : 'taker (stop-loss market)'}"><span class="material-symbols-outlined text-[0.7rem] leading-none">logout</span>${t.exit_maker ? 'maker' : 'taker'}</span>` : ''}</div>` : ''}
    </td>
    <td class="px-3 py-5">
      <div class="flex flex-col items-start gap-0.5">
        <span class="px-2 py-0.5 rounded text-[0.6rem] font-bold uppercase ${sideClass} border">${t.side === 'long' ? 'LONG' : 'SHORT'}</span>
        <span class="text-[0.55rem] font-bold uppercase tracking-wider ${statusClass}">${statusLabel}</span>
      </div>
    </td>
    <td class="px-3 py-5 text-right font-mono text-sm text-on-surface">${entry.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</td>
    <td class="px-3 py-5 text-right font-mono text-sm text-on-surface">${
      t.exit_price
        ? exit_.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
        : '<span class="text-outline">—</span>'
    }</td>
    <td class="px-3 py-5 text-right font-mono text-sm text-on-surface">${qty}</td>
    <td data-col="pnl" class="px-3 py-5 text-right font-mono text-sm ${pnlColor} font-bold">${pnlStr}</td>
  </tr>`;
}

export function renderTrades() {
  const tzMode = getTzMode();  // highlights the active Times pill (Local | UTC)
  const liveVaults = VAULT_CONFIGS.filter(v => v.status === 'live' && v.address);
  const vaultMenuItems = liveVaults.map(v => {
    const vi = VAULT_ICONS[v.key] || VAULT_ICONS.onyx;
    return `<button class="w-full flex items-center gap-2.5 px-3 py-2 rounded hover:bg-surface-container transition-colors text-sm text-on-surface" data-vault-filter="${v.name}">
      <span class="material-symbols-outlined ${vi.text} text-base">${vi.icon}</span> ${v.name}
    </button>`;
  }).join('');

  return `
<main class="w-full">
<header class="mb-12">
<h1 class="font-headline text-3xl font-bold tracking-tight text-on-surface mb-2">Vault Trading Activity</h1>
<p class="text-sm text-secondary/70 mb-8">These are the vault's trading results. For your personal return, see <a href="#overview" class="text-primary hover:underline">Overview</a>.</p>
<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
<div class="bg-surface-container-low p-6 rounded relative overflow-hidden group">
  <div class="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
  <div class="relative z-10">
    <p class="text-[0.75rem] uppercase tracking-widest text-secondary mb-2">Total Trades</p>
    <p class="font-headline text-4xl font-bold text-on-surface" id="stat-total-trades">—</p>
  </div>
</div>
<div id="stat-return-tile" class="bg-surface-container-low p-6 rounded border-l-4 border-primary-container shadow-[0_0_32px_rgba(34,211,238,0.05)]">
  <p class="text-[0.75rem] uppercase tracking-widest text-secondary mb-2" id="stat-return-label">Total Return</p>
  <p class="font-headline text-4xl font-bold text-primary-container" id="stat-total-return">—</p>
  <p class="text-[0.65rem] text-outline mt-1" id="stat-return-pct"></p>
</div>
<div class="bg-surface-container-low p-6 rounded" title="Win Rate — percentage of closed trades that finished profitable.">
  <p class="text-[0.75rem] uppercase tracking-widest text-secondary mb-2">Win Rate</p>
  <p class="font-headline text-4xl font-bold text-on-surface tabular-nums" id="stat-hit-rate">—</p>
</div>
<div class="bg-surface-container-low p-6 rounded" title="Average time each closed trade was open from entry to exit.">
  <p class="text-[0.75rem] uppercase tracking-widest text-secondary mb-2">Avg. Holding Time</p>
  <p class="font-headline text-4xl font-bold text-on-surface tabular-nums" id="stat-avg-hold">—</p>
</div>
</div>
</header>

<div class="flex gap-2 mb-6" id="trade-filter-pills">
  <button class="px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-lg bg-primary text-on-primary transition-all trade-filter-btn" data-filter="all">All</button>
  <button class="px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-lg bg-surface-container-high text-secondary hover:text-on-surface transition-all trade-filter-btn" data-filter="open">Open</button>
  <button class="px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-lg bg-surface-container-high text-secondary hover:text-on-surface transition-all trade-filter-btn" data-filter="won">Won</button>
  <button class="px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-lg bg-surface-container-high text-secondary hover:text-on-surface transition-all trade-filter-btn" data-filter="lost">Lost</button>
</div>

<section class="mb-6 flex flex-col md:flex-row gap-4 items-end justify-between bg-surface-container/40 p-6 rounded-lg backdrop-blur-sm border border-outline-variant/10">
  <div class="flex flex-col md:flex-row gap-4 w-full md:w-auto">
    <div class="flex flex-col gap-2">
      <label class="text-[0.75rem] uppercase tracking-widest text-secondary ml-1">Search Asset</label>
      <div class="relative">
        <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline">search</span>
        <input id="trades-search-input" class="bg-surface-container-lowest border-none ring-1 ring-outline-variant/30 rounded px-10 py-2.5 w-full md:w-64 focus:ring-primary-container text-sm transition-all outline-none" placeholder="e.g. BTC, SOL..." type="text" />
      </div>
    </div>
    <div class="flex flex-col gap-2">
      <label class="text-[0.75rem] uppercase tracking-widest text-secondary ml-1">Vault</label>
      <div class="relative" id="trades-vault-dropdown">
        <button class="bg-surface-container-lowest ring-1 ring-outline-variant/30 rounded px-4 py-2.5 min-w-[200px] flex items-center justify-between gap-3 text-sm hover:ring-primary/40 transition-all cursor-pointer" id="trades-vault-btn">
          <div class="flex items-center gap-2">
            <span class="material-symbols-outlined text-primary text-base">grid_view</span>
            <span id="trades-vault-label">All Vaults</span>
          </div>
          <span class="material-symbols-outlined text-outline text-sm">expand_more</span>
        </button>
        <div class="absolute top-full left-0 mt-1 w-full bg-surface-container-low border border-outline-variant/20 rounded-lg shadow-xl z-50 hidden" id="trades-vault-menu">
          <div class="p-1">
            <button class="w-full flex items-center gap-2.5 px-3 py-2 rounded hover:bg-surface-container transition-colors text-sm text-on-surface" data-vault-filter="all">
              <span class="material-symbols-outlined text-primary text-base">grid_view</span> All Vaults
            </button>
            ${vaultMenuItems}
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-2">
      <label class="text-[0.75rem] uppercase tracking-widest text-secondary ml-1">Timeframe</label>
      <div class="flex gap-1" id="trades-timeframe-btns">
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary" data-tf="4">4H</button>
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary" data-tf="24">24H</button>
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors bg-primary text-on-primary" data-tf="168">7D</button>
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary" data-tf="720">30D</button>
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary" data-tf="mtd">MTD</button>
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary" data-tf="0">All</button>
      </div>
    </div>
    <div class="flex flex-col gap-2">
      <label class="text-[0.75rem] uppercase tracking-widest text-secondary ml-1">Times</label>
      <div class="flex gap-1" id="trades-tz-btns">
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors ${tzMode === 'local' ? 'bg-primary text-on-primary' : 'bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary'}" data-tz="local">Local</button>
        <button class="px-3 py-2 rounded text-xs font-bold transition-colors ${tzMode === 'utc' ? 'bg-primary text-on-primary' : 'bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary'}" data-tz="utc">UTC</button>
      </div>
    </div>
  </div>
  <button id="trades-export-csv" class="bg-primary-container text-on-primary-container px-6 py-2.5 rounded font-medium text-sm flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-primary-container/20">
    <span class="material-symbols-outlined">download</span>
    Export CSV
  </button>
</section>

<div class="bg-surface-container-low rounded-xl overflow-hidden shadow-2xl">
  <div class="overflow-x-auto">
    <table class="w-full text-left border-collapse">
      <thead>
        <tr class="bg-surface-container-high/50 text-secondary uppercase text-[0.65rem] tracking-[0.2em] font-bold">
          <th class="px-3 py-5 whitespace-nowrap cursor-pointer hover:text-primary transition-colors" data-sort="date">Open Time <span class="material-symbols-outlined" style="font-size:14px;opacity:0.35;vertical-align:middle;line-height:1">swap_vert</span></th>
          <th class="px-3 py-5 whitespace-nowrap cursor-pointer hover:text-primary transition-colors" data-sort="close">Close Time <span class="material-symbols-outlined" style="font-size:14px;opacity:0.35;vertical-align:middle;line-height:1">swap_vert</span></th>
          <th class="px-3 py-5 whitespace-nowrap cursor-pointer hover:text-primary transition-colors" data-sort="vault">Vault <span class="material-symbols-outlined" style="font-size:14px;opacity:0.35;vertical-align:middle;line-height:1">swap_vert</span></th>
          <th class="px-3 py-5 whitespace-nowrap cursor-pointer hover:text-primary transition-colors" data-sort="asset">Asset <span class="material-symbols-outlined" style="font-size:14px;opacity:0.35;vertical-align:middle;line-height:1">swap_vert</span></th>
          <th class="px-3 py-5 whitespace-nowrap w-1">Side</th>
          <th class="px-3 py-5 whitespace-nowrap text-right">Entry Price</th>
          <th class="px-3 py-5 whitespace-nowrap text-right">Exit Price</th>
          <th class="px-3 py-5 whitespace-nowrap text-right">Quantity</th>
          <th data-col="pnl" class="px-3 py-5 whitespace-nowrap text-right cursor-pointer hover:text-primary transition-colors" data-sort="pnl">P/L <span class="material-symbols-outlined" style="font-size:14px;opacity:0.35;vertical-align:middle;line-height:1">swap_vert</span></th>
        </tr>
      </thead>
      <tbody id="trades-table-body" class="divide-y divide-outline-variant/5">
        <tr><td colspan="9" class="px-6 py-12 text-center text-secondary">
          <span class="material-symbols-outlined text-4xl text-secondary/30 block mb-3">hourglass_empty</span>
          Loading trades...
        </td></tr>
      </tbody>
    </table>
  </div>
  <div class="px-3 py-5 bg-surface-container-high/30 flex items-center justify-between border-t border-outline-variant/10">
    <span class="text-xs text-outline uppercase tracking-wider" id="trades-pagination-info">Loading...</span>
    <div class="flex gap-2" id="trades-pagination-btns"></div>
  </div>
</div>
</main>
`;
}

export async function initTrades() {
  // Stealth gate: non-depositors don't see per-trade P&L or the Total Return
  // stat tile. Depositors get the full public view. Inject a CSS rule to hide
  // data-col="pnl" elements — survives re-renders in renderPage().
  const addr = getAddress();
  const hidePnl = isStealth() && !(addr && await isDepositor(addr));
  if (hidePnl && !document.getElementById("stealth-pnl-hide")) {
    const style = document.createElement("style");
    style.id = "stealth-pnl-hide";
    style.textContent = `
      [data-col="pnl"] { display: none !important; }
      .trade-filter-btn[data-filter="won"],
      .trade-filter-btn[data-filter="lost"] { display: none !important; }
    `;
    document.head.appendChild(style);
    const tile = document.getElementById("stat-return-tile");
    if (tile) {
      tile.innerHTML = `
        <p class="text-[0.75rem] uppercase tracking-widest text-secondary mb-2">Performance</p>
        <p class="font-headline text-2xl font-bold text-primary">Opens ${launchDateLabel()}</p>
        <p class="text-[0.65rem] text-outline mt-1">Live trading visible below</p>
      `;
    }
  }

  // Warm BOTH HL batches in ONE parallel wave before fetchTrades + enrichment
  // read them. Previously this page fired ~10 concurrent /api/hl/info POSTs in
  // TWO serial waves (5 userFills, then 5 clearinghouseState) — each its own
  // Lambda invocation, spilling provisioned concurrency into cold starts
  // (~3-7s wall observed). Two batched calls (server fans out in-process)
  // collapse that to ~1s. fetchTrades()'s getFills() + the enrichment's
  // getVaultState() below now both hit the warmed hlFeed cache.
  const liveVaults = VAULT_CONFIGS.filter(v => v.status === 'live' && v.address);
  const liveAddrs = liveVaults.map(v => v.address);
  await Promise.all([
    prefetchFills(liveAddrs).catch(() => {}),
    prefetchVaultStates(liveAddrs).catch(() => {}),
  ]);

  const trades = await fetchTrades();

  // Enrich open trades with live floating P&L from HL clearinghouseState,
  // AND sync each open trade's size to HL's current position (handles TP partials).
  // Shared hlFeed cache means Overview page reads the SAME state here.
  try {
    // getVaultState() is now a cache hit (warmed by prefetchVaultStates above).
    const states = await Promise.all(liveVaults.map(async (vault) => {
      try { return { vault, state: await getVaultState(vault.address) }; }
      catch (_) { return null; }
    }));
    for (const item of states) {
      if (!item) continue;
      const { vault, state } = item;
      const posMap = {};
      for (const entry of (state.assetPositions || [])) {
        const pos = entry.position || {};
        posMap[pos.coin] = {
          floating_pnl: parseFloat(pos.unrealizedPnl || 0),
          entry_price:  parseFloat(pos.entryPx || 0),
          size: Math.abs(parseFloat(pos.szi || 0)),
          current_price: parseFloat(pos.positionValue || 0) / Math.abs(parseFloat(pos.szi || 1)),
        };
      }
      // Reconcile open-trade row against HL's authoritative view.
      // If HL has no position → drop the open row (it fully closed, just not in our
      // fill pairing yet). If HL size differs from our tracked remaining → adopt HL.
      const openIdxs = [];
      trades.forEach((t, i) => {
        if (t.status === 'open' && t.vault === vault.name) openIdxs.push(i);
      });
      for (const i of openIdxs) {
        const t = trades[i];
        const hl = posMap[t.coin];
        if (!hl || hl.size < 1e-9) {
          // HL no longer has this position — mark as closed with the floating PnL we had
          t.status = 'stale_open';  // filtered out of visible list below
          continue;
        }
        t.floating_pnl = hl.floating_pnl;
        if (Math.abs(hl.size - t.size) > 1e-6) {
          t.size = hl.size;
          t.notional = t.entry_price * hl.size;
        }
      }
    }
  } catch (e) { console.warn('Floating P&L enrichment failed:', e); }

  // Remove stale opens (HL no longer has them) so they don't pollute display + stats
  for (let i = trades.length - 1; i >= 0; i--) {
    if (trades[i].status === 'stale_open') trades.splice(i, 1);
  }

  // Net P&L is computed by summing the rows below. No hidden NAV formula —
  // what users see on the table adds up to the headline, period.

  const tbody = document.getElementById('trades-table-body');
  if (!tbody) return;

  if (trades.length === 0) {
    tbody.innerHTML = `<tr><td colspan="8" class="px-6 py-16 text-center text-secondary">
      <span class="material-symbols-outlined text-5xl text-secondary/20 block mb-4">receipt_long</span>
      <p class="font-headline font-bold text-on-surface mb-1">No trades yet</p>
      <p class="text-sm text-secondary">Trades will appear here once the vault executes strategies.</p>
    </td></tr>`;
    ['stat-total-trades','stat-net-pnl','stat-hit-rate','stat-avg-hold'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.textContent = id === 'stat-total-trades' ? '0' : '—';
    });
    document.getElementById('trades-pagination-info').textContent = 'No trades';
    return;
  }

  const PAGE_SIZE = 50;
  let currentPage = 0;
  let sortedTrades = [...trades];

  function renderPage() {
    const start = currentPage * PAGE_SIZE;
    const page = sortedTrades.slice(start, start + PAGE_SIZE);
    tbody.innerHTML = page.map(t => formatTradeRow(t)).join('');

    const totalPages = Math.ceil(sortedTrades.length / PAGE_SIZE);
    const paginationEl = document.getElementById('trades-pagination-info');
    if (paginationEl) paginationEl.textContent = `${start + 1}–${Math.min(start + PAGE_SIZE, sortedTrades.length)} of ${sortedTrades.length} trades`;

    const paginationBtns = document.getElementById('trades-pagination-btns');
    if (paginationBtns && totalPages > 1) {
      let html = `<button class="w-8 h-8 flex items-center justify-center rounded bg-surface-container text-outline hover:text-primary transition-colors ring-1 ring-outline-variant/20" data-page="${Math.max(0, currentPage - 1)}"><span class="material-symbols-outlined text-sm">chevron_left</span></button>`;
      for (let i = 0; i < totalPages; i++) {
        const active = i === currentPage;
        html += `<button class="w-8 h-8 flex items-center justify-center rounded text-xs font-bold ring-1 ${active ? 'bg-primary text-on-primary ring-primary' : 'bg-surface-container text-on-surface hover:text-primary ring-outline-variant/20'} transition-colors" data-page="${i}">${i + 1}</button>`;
      }
      html += `<button class="w-8 h-8 flex items-center justify-center rounded bg-surface-container text-outline hover:text-primary transition-colors ring-1 ring-outline-variant/20" data-page="${Math.min(totalPages - 1, currentPage + 1)}"><span class="material-symbols-outlined text-sm">chevron_right</span></button>`;
      paginationBtns.innerHTML = html;
      paginationBtns.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => { currentPage = parseInt(btn.dataset.page); renderPage(); });
      });
    } else if (paginationBtns) { paginationBtns.innerHTML = ''; }
  }

  // Sort columns
  document.querySelectorAll('th[data-sort]').forEach(th => {
    let asc = false;
    th.addEventListener('click', () => {
      asc = !asc;
      const key = th.dataset.sort;
      sortedTrades.sort((a, b) => {
        let va, vb;
        if (key === 'date')  { va = new Date(a.ts || 0).getTime();           vb = new Date(b.ts || 0).getTime(); }
        else if (key === 'close') { va = new Date(a.close_ts || '2099').getTime(); vb = new Date(b.close_ts || '2099').getTime(); }
        else if (key === 'vault') { va = a.vault || '';                        vb = b.vault || ''; }
        else if (key === 'asset') { va = a.coin || '';                         vb = b.coin || ''; }
        else if (key === 'pnl')   { va = rowPnl(a);                            vb = rowPnl(b); }
        else return 0;
        if (typeof va === 'string') return asc ? va.localeCompare(vb) : vb.localeCompare(va);
        return asc ? va - vb : vb - va;
      });
      currentPage = 0;
      renderPage();
    });
  });

  // Times toggle (Local ⇄ UTC). Persisted globally; re-render the current page
  // rows so open/close times flip immediately without re-fetching.
  const tzBtns = document.getElementById('trades-tz-btns');
  if (tzBtns) {
    tzBtns.querySelectorAll('button').forEach(btn => {
      btn.addEventListener('click', () => {
        setTzMode(btn.dataset.tz);
        tzBtns.querySelectorAll('button').forEach(b => {
          const on = b.dataset.tz === getTzMode();
          b.className = `px-3 py-2 rounded text-xs font-bold transition-colors ${on ? 'bg-primary text-on-primary' : 'bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary'}`;
        });
        renderPage();
      });
    });
  }

  // Filter state
  let activeStatusFilter = 'all';
  let activeVaultFilter = 'all';
  let activeTimeframeHours = 168;
  let activeTimeframeMtd = false;
  let activeAssetQuery = '';

  function getFilteredTrades() {
    let filtered = [...trades];
    if (activeTimeframeMtd) {
      const now = new Date();
      const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      filtered = filtered.filter(t => new Date(t.ts) >= monthStart);
    } else if (activeTimeframeHours > 0) {
      const cutoff = new Date(Date.now() - activeTimeframeHours * 3600000);
      filtered = filtered.filter(t => new Date(t.ts) >= cutoff);
    }
    if (activeVaultFilter !== 'all') {
      filtered = filtered.filter(t => (t.vault || '').toLowerCase() === activeVaultFilter.toLowerCase());
    }
    if (activeAssetQuery) {
      filtered = filtered.filter(t => (t.coin || '').toUpperCase().includes(activeAssetQuery));
    }
    if (activeStatusFilter !== 'all') {
      filtered = filtered.filter(t => {
        if (activeStatusFilter === 'open') return t.status === 'open';
        if (activeStatusFilter === 'won')  return t.status !== 'open' && rowPnl(t) > 0;
        if (activeStatusFilter === 'lost') return t.status !== 'open' && rowPnl(t) <= 0;
        return true;
      });
    }
    return filtered;
  }

  async function updateStats(filtered) {
    const totalEl  = document.getElementById('stat-total-trades');
    const retEl    = document.getElementById('stat-total-return');
    const pctEl    = document.getElementById('stat-return-pct');
    const hitEl    = document.getElementById('stat-hit-rate');
    const holdEl   = document.getElementById('stat-avg-hold');

    if (totalEl) totalEl.textContent = filtered.length;

    // Stealth: the return tile was swapped to "Opens <date>" above. Skip
    // all of the on-chain NAV/PnL work — its outputs have no DOM sink and
    // the RPC loop wastes time.
    if (hidePnl) return;

    // Show on-chain Total Return when all trades are visible (regardless of
    // which timeframe button is active — 7D on a 4-day-old vault = all trades).
    const isAllTradesVisible = filtered.length === trades.length;
    const labelEl = document.getElementById('stat-return-label');
    if (labelEl) labelEl.textContent = isAllTradesVisible ? 'Total Return' : 'Net P&L';

    // Disconnected: the user-scoped P&L number is meaningless (no holdings).
    // Show "—" and skip the compute — everything else on this page (trades
    // list, trade count, hit rate, hold time) is public and stays visible.
    const userConnected = !!getAddress();
    if (!userConnected) {
      if (retEl) {
        retEl.textContent = '—';
        retEl.className = 'font-headline text-4xl font-bold text-on-surface-variant';
      }
      if (pctEl) pctEl.textContent = 'Connect wallet to see your return';
    } else if (isAllTradesVisible) {
      // "All" view: show on-chain Total Return (matches Overview exactly).
      // Was TWO sequential `for…of` loops — first for value/cost, then for
      // open PnL — 6 vaults × 2 round-trips × 2 loops ≈ ~3.6s. Now one
      // Promise.all firing balanceOf, userCostBasis, getVaultState concurrently
      // per vault (and in parallel across vaults), then a sequential
      // convertToAssets only for vaults with shares > 0. ~max(600ms) wall.
      try {
        const addr = getAddress();
        const rp = getReadProvider();
        if (addr && rp) {
          const liveVcs = VAULT_CONFIGS.filter(v => v.status === 'live' && v.address);
          const perVault = await Promise.all(liveVcs.map(async (vc) => {
            const vault = new ethers.Contract(vc.address, VAULT_ABI, rp);
            try {
              const [shares, costBasis, state] = await Promise.all([
                vault.balanceOf(addr),
                vault.userCostBasis(addr),
                getVaultState(vc.address).catch(() => null),
              ]);
              let value = 0;
              if (shares > 0n) {
                value = Number(await vault.convertToAssets(shares)) / 10 ** USDC_DECIMALS;
              }
              const cost = Number(costBasis) / 10 ** USDC_DECIMALS;
              let openPnl = 0;
              if (state && state.assetPositions) {
                for (const ap of state.assetPositions) {
                  openPnl += parseFloat(ap.position?.unrealizedPnl || 0);
                }
              }
              return { value, cost, openPnl };
            } catch (e) {
              console.warn(`Total Return: ${vc.name} fetch failed:`, e);
              return { value: 0, cost: 0, openPnl: 0 };
            }
          }));
          let totalValue = 0, totalCost = 0, openPnl = 0;
          for (const r of perVault) {
            totalValue += r.value;
            totalCost += r.cost;
            openPnl += r.openPnl;
          }
          const userReturn = totalValue - totalCost;
          if (retEl) {
            retEl.textContent = (userReturn >= 0 ? '+' : '-') + '$' + Math.abs(userReturn).toFixed(2);
            retEl.className = `font-headline text-4xl font-bold ${userReturn >= 0 ? 'text-tertiary' : 'text-error'}`;
          }
          if (pctEl) {
            const realizedPnl = userReturn - openPnl;
            const rStr = (realizedPnl >= 0 ? '+' : '-') + '$' + Math.abs(realizedPnl).toFixed(2) + ' realized';
            const uStr = (openPnl >= 0 ? '+' : '-') + '$' + Math.abs(openPnl).toFixed(2) + ' open';
            pctEl.textContent = openPnl !== 0 ? `${rStr} · ${uStr}` : rStr;
          }
        }
      } catch (e) {
        console.warn('Total Return fetch failed:', e);
      }
    } else {
      // Filtered view: sum visible trade P&Ls (matches the rows the user sees)
      let realized = 0, openPnl = 0;
      filtered.forEach(t => {
        if (t.status === 'open') openPnl += rowPnl(t);
        else                     realized += rowPnl(t);
      });
      const netPnl = realized + openPnl;
      if (retEl) {
        retEl.textContent = (netPnl >= 0 ? '+' : '-') + '$' + Math.abs(netPnl).toFixed(2);
        retEl.className = `font-headline text-4xl font-bold ${netPnl >= 0 ? 'text-tertiary' : 'text-error'}`;
      }
      if (pctEl) {
        const rStr = (realized >= 0 ? '+' : '-') + '$' + Math.abs(realized).toFixed(2) + ' realized';
        const uStr = (openPnl >= 0 ? '+' : '-') + '$' + Math.abs(openPnl).toFixed(2) + ' open';
        pctEl.textContent = openPnl !== 0 ? `${rStr} · ${uStr}` : rStr;
      }
    }

    const completed = filtered.filter(t => t.status !== 'open');
    const wins = completed.filter(t => rowPnl(t) > 0).length;
    if (hitEl) hitEl.textContent = completed.length > 0 ? Math.round(wins / completed.length * 100) + '%' : '—';

    const holdTimes = completed
      .filter(t => t.ts && t.close_ts)
      .map(t => new Date(t.close_ts) - new Date(t.ts))
      .filter(ms => ms > 0);
    if (holdEl) {
      if (holdTimes.length > 0) {
        const hrs = holdTimes.reduce((s, v) => s + v, 0) / holdTimes.length / 3600000;
        holdEl.textContent = hrs < 1 ? Math.round(hrs * 60) + 'm' : hrs < 24 ? hrs.toFixed(1) + 'h' : (hrs / 24).toFixed(1) + 'd';
      } else { holdEl.textContent = '—'; }
    }
  }

  function applyFilters() {
    const filtered = getFilteredTrades();
    sortedTrades = filtered;
    currentPage = 0;
    updateStats(filtered);
    renderPage();
  }

  applyFilters();

  // Filter pills
  document.querySelectorAll('.trade-filter-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      activeStatusFilter = e.target.dataset.filter;
      document.querySelectorAll('.trade-filter-btn').forEach(b => {
        b.className = 'px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-lg bg-surface-container-high text-secondary hover:text-on-surface transition-all trade-filter-btn';
      });
      e.target.className = 'px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-lg bg-primary text-on-primary transition-all trade-filter-btn';
      applyFilters();
    });
  });

  // Vault dropdown
  const vaultBtn  = document.getElementById('trades-vault-btn');
  const vaultMenu = document.getElementById('trades-vault-menu');
  const vaultLabel = document.getElementById('trades-vault-label');
  if (vaultBtn && vaultMenu) {
    vaultBtn.addEventListener('click', () => vaultMenu.classList.toggle('hidden'));
    document.addEventListener('click', (e) => {
      if (!vaultBtn.contains(e.target) && !vaultMenu.contains(e.target)) vaultMenu.classList.add('hidden');
    });
    vaultMenu.querySelectorAll('[data-vault-filter]').forEach(item => {
      item.addEventListener('click', () => {
        activeVaultFilter = item.dataset.vaultFilter;
        vaultLabel.textContent = activeVaultFilter === 'all' ? 'All Vaults' : activeVaultFilter;
        vaultMenu.classList.add('hidden');
        applyFilters();
      });
    });
  }

  // Timeframe
  const tfContainer = document.getElementById('trades-timeframe-btns');
  if (tfContainer) {
    tfContainer.querySelectorAll('button').forEach(btn => {
      btn.addEventListener('click', () => {
        tfContainer.querySelectorAll('button').forEach(b => {
          b.className = 'px-3 py-2 rounded text-xs font-bold transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/30 text-on-surface-variant hover:text-primary';
        });
        btn.className = 'px-3 py-2 rounded text-xs font-bold transition-colors bg-primary text-on-primary';
        if (btn.dataset.tf === 'mtd') { activeTimeframeMtd = true; activeTimeframeHours = 0; }
        else { activeTimeframeMtd = false; activeTimeframeHours = parseInt(btn.dataset.tf); }
        applyFilters();
      });
    });
  }

  // Asset search
  const searchInput = document.getElementById('trades-search-input');
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      activeAssetQuery = searchInput.value.toUpperCase().trim();
      applyFilters();
    });
  }

  // Export CSV
  document.getElementById('trades-export-csv')?.addEventListener('click', () => {
    const filtered = getFilteredTrades();
    // Stealth non-depositor: omit the P&L column from export so the on-screen
    // stealth state isn't trivially bypassed by clicking Download.
    const header = ['Open Time','Close Time','Vault','Asset','Side','Status','Entry Price','Exit Price','Quantity'];
    if (!hidePnl) header.push('P&L');
    header.push('Entry Maker', 'Exit Maker');
    const rows = [header];
    filtered.forEach(t => {
      const displayPnl = rowPnl(t);
      const row = [
        t.ts, t.close_ts || '', t.vault, t.coin,
        t.side, t.status,
        t.entry_price, t.exit_price || '', t.size,
      ];
      if (!hidePnl) {
        row.push((t.pnl != null || t.status === 'open') ? displayPnl.toFixed(4) : '');
      }
      row.push(t.entry_maker == null ? '' : (t.entry_maker ? 'Yes' : 'No'),
               t.exit_maker == null ? '' : (t.exit_maker ? 'Yes' : 'No'));
      rows.push(row);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `crystalvault_trades_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  });
}
